// K9-019A — destination contract for new ordinary "Pagamento" movements (mirrors backend checks).
export const DESTINATIONS = [
  { value: "SESSION", label: "Prestazione" },
  { value: "PACKAGE", label: "Pacchetto" },
  { value: "OTHER", label: "Altro incasso" },
];

export const initialDestination = ({ presetSessionId, presetPackage } = {}) =>
  presetSessionId ? "SESSION" : presetPackage?.id ? "PACKAGE" : "";

const cents = (x) => Math.round(Number(x) * 100);
export const MAX_AMOUNT = 1000000;

// String-based check (no float tolerance, no silent rounding); mirrors backend money_amount().
export const amountError = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Inserisci un importo valido maggiore di zero.";
  if (!/^\d+(\.\d+)?$/.test(raw)) return "Inserisci un importo valido maggiore di zero.";
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return "L'importo può avere al massimo due decimali (minimo € 0,01).";
  const n = Number(raw);
  if (!(n >= 0.01)) return "Inserisci un importo valido maggiore di zero.";
  if (n > MAX_AMOUNT) return "L'importo supera il massimo consentito (€ 1.000.000,00).";
  return null;
};

const sessionFits = (s, owner, dog) => !!s && s.owner_id === owner && (!dog || (s.dog_ids || []).includes(dog));
const packageFits = (p, owner, dog) => !!p && p.owner_id === owner && (!dog || p.dog_id === dog);

// Owner/dog change: keep linked targets only while still compatible; clear stale ones (and an auto-filled amount).
export const applyOwnerDog = (f, patch, { sessions = [], packages = [] } = {}) => {
  const n = { ...f, ...patch };
  const keepS = !!f.session_id && sessionFits(sessions.find((s) => s.id === f.session_id), n.owner_id, n.dog_id);
  const keepP = !!f.package_id && packageFits(packages.find((p) => p.id === f.package_id), n.owner_id, n.dog_id);
  if (!keepS) n.session_id = "";
  if (!keepP) n.package_id = "";
  if (((f.session_id && !keepS) || (f.package_id && !keepP)) && !f._amtTouched) n.amount = "";
  return n;
};

export const CREDIT_TYPE = "Credito cliente";
export const hasLinks = (type) => type !== CREDIT_TYPE;
export const notesLabel = (type, dest) => (type === "Rettifica" ? "Note / Motivo *" : dest === "OTHER" ? "Causale *" : "Note");

// Type change: a client-credit grant is owner-level (no links); leaving it never resurrects previous links.
export const applyType = (f, type, presetLinks) => {
  if (type === CREDIT_TYPE || f.type === CREDIT_TYPE) return { ...f, type, session_id: "", package_id: "", destination: "" };
  return { ...f, type, ...presetLinks };
};

// Returns an Italian error message, or null when the form is valid.
export const validatePaymentForm = (form, residual) => {
  if (!form.owner_id) return "Seleziona un proprietario.";
  const amtErr = amountError(form.amount);
  if (amtErr) return amtErr;
  const n = Number(String(form.amount).trim());
  if (form.session_id && form.package_id) return "Collega il movimento a una prestazione oppure a un pacchetto, non a entrambi.";
  if (form.type === "Rettifica" && !(form.notes || "").trim()) return "Per una rettifica indica una nota / motivo.";
  if (form.type === CREDIT_TYPE && (form.session_id || form.package_id)) return "Il credito cliente non va collegato a prestazioni o pacchetti.";
  if (form.type !== "Pagamento") return null;
  if (!form.destination) return "Scegli la destinazione: prestazione, pacchetto o altro incasso.";
  if (form.destination === "SESSION" && !form.session_id) return "Seleziona la prestazione da incassare.";
  if (form.destination === "PACKAGE" && !form.package_id) return "Seleziona il pacchetto da incassare.";
  if (form.destination === "OTHER" && !(form.notes || "").trim()) return "Per un altro incasso indica la causale.";
  if (form.destination !== "OTHER" && residual != null && cents(n) > cents(residual)) return "L'importo supera il residuo da incassare.";
  return null;
};

export const buildPaymentPayload = (form) => {
  const isPay = form.type === "Pagamento";
  const dest = isPay ? form.destination : null;
  return {
    date: form.date, owner_id: form.owner_id, dog_id: form.dog_id || null,
    session_id: (isPay ? dest === "SESSION" : hasLinks(form.type)) ? (form.session_id || null) : null,
    package_id: (isPay ? dest === "PACKAGE" : hasLinks(form.type)) ? (form.package_id || null) : null,
    amount: Number(form.amount), method: form.method, type: form.type, notes: form.notes || "",
    ...(isPay ? { destination_type: dest } : {}),
  };
};
